import { twMerge } from "tailwind-merge";
import { Type } from "@themes/pollinator/ui/Type";
import type { SchedulingMode } from "@core/features/actions";

export type Props = {
  className?: string;
  mode: SchedulingMode;
  // Freeform mode
  minRequiredHours?: number;
  minContiguousDuration?: number;
  betterMatchHours?: number;
  // Options modes
  requiredSelections?: number;
  // Optional deadline (shown for all modes if provided)
  deadline?: string;
};

export function SchedulingInstructions({
  className,
  mode,
  minRequiredHours = 10,
  minContiguousDuration = 2,
  betterMatchHours,
  requiredSelections = 1,
  deadline,
}: Props) {
  const effectiveBetterMatchHours = betterMatchHours ?? minRequiredHours + 4;

  return (
    <div className={twMerge("flex flex-col gap-4 px-4", className)}>
      <Type variant="h4">Instructions</Type>

      {mode === "freeform" && (
        <>
          <Type variant="body2">
            Choose at least {minRequiredHours} hours over the week (in{" "}
            {minContiguousDuration}+ hour slots). For best matching results,
            select {effectiveBetterMatchHours}+ hours.
          </Type>
          <Type variant="body2">
            In the calendar grid, tap on a cell to indicate your availability.
            Tap and drag to select multiple slots. Slots are only valid when
            they are {minContiguousDuration} hours or more.
          </Type>
        </>
      )}

      {mode === "select-options" && (
        <>
          <Type variant="body2">
            Select {requiredSelections} option{requiredSelections !== 1 ? "s" : ""}{" "}
            from the highlighted time blocks.
          </Type>
          <Type variant="body2">
            Tap on a highlighted block to select it.
          </Type>
        </>
      )}

      {mode === "repeating" && (
        <>
          <Type variant="body2">
            Select a time slot. All slots at that time across all days will be
            selected.
          </Type>
          <Type variant="body2">
            Tap on any highlighted block to select that time for all days.
          </Type>
        </>
      )}

      {deadline && (
        <Type variant="body2">
          You can add to or edit these selections up until {deadline}.
        </Type>
      )}
    </div>
  );
}

export default SchedulingInstructions;
